/**
 * Sistema de deduplicación de requests para evitar llamadas API simultáneas
 * Mantiene una caché temporal de requests activos para evitar duplicados
 */

const activeRequests = new Map();
const requestPromises = new Map();

/**
 * Deduplica requests basados en una clave única
 * Si ya existe un request con la misma clave, devuelve la promesa existente
 * @param {string} key - Clave única para identificar el request
 * @param {Function} requestFn - Función que ejecuta el request
 * @returns {Promise} - Promesa del request (nueva o existente)
 */
export const deduplicateRequest = (key, requestFn) => {
  // Si ya existe un request activo con esta clave, devolver la promesa existente
  if (activeRequests.has(key)) {
    return activeRequests.get(key);
  }
  
  // Crear nueva promesa y registrarla
  const promise = requestFn()
    .finally(() => {
      // Limpiar el request de la caché cuando termine
      activeRequests.delete(key);
      requestPromises.delete(key);
    });
  
  // Guardar la promesa para futuros requests duplicados
  activeRequests.set(key, promise);
  requestPromises.set(key, promise);
  
  return promise;
};

/**
 * Cancela un request específico si está activo
 * @param {string} key - Clave del request a cancelar
 */
export const cancelRequest = (key) => {
  if (activeRequests.has(key)) {
    activeRequests.delete(key);
    requestPromises.delete(key);
  }
};

/**
 * Verifica si un request está actualmente ejecutándose
 * @param {string} key - Clave del request
 * @returns {boolean} - True si el request está activo
 */
export const isRequestActive = (key) => {
  return activeRequests.has(key);
};

/**
 * Limpia todas las requests activas (útil para cleanup)
 */
export const clearAllRequests = () => {
  activeRequests.clear();
  requestPromises.clear();
};

/**
 * Hook para crear claves únicas de deduplicación basadas en parámetros
 * @param {string} baseKey - Clave base
 * @param {Object} params - Parámetros adicionales para la clave
 * @returns {string} - Clave única
 */
export const createRequestKey = (baseKey, params = {}) => {
  const paramString = Object.keys(params)
    .sort()
    .map(key => `${key}:${params[key]}`)
    .join('|');
  
  return paramString ? `${baseKey}|${paramString}` : baseKey;
};